<template>
  <div :class="['wdp-panel', { 'wdp-excluded': isExcluded }]">
    <!-- Collapsible header -->
    <div class="wdp-hdr" @click="expanded = !expanded">
      <div class="wdp-hdr-left">
        <span class="wdp-chevron">{{ expanded ? '▼' : '▶' }}</span>
        <div>
          <div class="wdp-title">
            <span class="wdp-wono">{{ wo.wono }}</span>
            <span class="wdp-sep"> — </span>
            <span class="wdp-wo-desc">{{ wo.ProductName }}</span>
          </div>
          <div class="wdp-sub">
            <span>{{ woStatusLabel(wo.woStatus) }}</span>
            <template v-if="wo.woDate"> · {{ formatDate(wo.woDate) }}</template>
            <span> · {{ wo.lines.length }} {{ wo.lines.length === 1 ? 'material line' : 'material lines' }}</span>
          </div>
        </div>
      </div>
      <div class="wdp-hdr-right">
        <template v-if="isExcluded">
          <span class="wdp-badge wdp-badge-excluded">Excluded</span>
        </template>
        <template v-else-if="hasNoChanges">
          <span class="wdp-nochange-text">No changes</span>
        </template>
        <template v-else>
          <span
            v-for="(pill, i) in summaryPills"
            :key="i"
            :class="['wdp-pill', 'wdp-pill-' + pill.color]"
          >{{ pill.text }}</span>
        </template>
      </div>
    </div>

    <!-- Expanded body -->
    <div v-if="expanded" class="wdp-body">
      <div class="wdp-dtable-wrap">
        <div class="wdp-dtable">
          <!-- Header row -->
          <div class="wdp-dtable-head">
            <div class="wdp-dth wdp-col-process">Process</div>
            <div class="wdp-dth wdp-col-code">Product</div>
            <div class="wdp-dth wdp-col-desc">Description</div>
            <div class="wdp-dth wdp-col-qty wdp-text-right">Current Unit Qty</div>
            <div class="wdp-dth wdp-col-qty wdp-text-right">New Unit Qty</div>
            <div class="wdp-dth wdp-col-uom">UOM</div>
            <div class="wdp-dth wdp-col-chg">Change</div>
          </div>
          <!-- Data rows -->
          <div
            v-for="(line, i) in wo.lines"
            :key="i"
            :class="['wdp-dtr', rowClass(line)]"
          >
            <!-- Process Code -->
            <div class="wdp-dtc wdp-col-process">
              <span>{{ line.processCode || '—' }}</span>
            </div>
            <!-- Code -->
            <div class="wdp-dtc wdp-col-code">
              <template v-if="isMaterialChanged(line)">
                <div class="wdp-old-val">{{ line.oldMaterialCode }}</div>
                <div class="wdp-new-code">{{ line.newMaterialCode }}</div>
              </template>
              <template v-else>
                <span>{{ line.oldMaterialCode || '—' }}</span>
              </template>
            </div>
            <!-- Description -->
            <div class="wdp-dtc wdp-col-desc">
              <template v-if="isMaterialChanged(line)">
                <div class="wdp-old-val">{{ line.oldDescription }}</div>
                <div class="wdp-new-desc">{{ line.newDescription }}</div>
              </template>
              <template v-else>
                <span>{{ line.oldDescription || '—' }}</span>
              </template>
            </div>
            <!-- Current Unit Qty -->
            <div class="wdp-dtc wdp-col-qty wdp-text-right">
              <span v-if="line.oldUnitQty != null">{{ formatQty(line.oldUnitQty) }}</span>
              <span v-else class="wdp-muted">—</span>
            </div>
            <!-- New Unit Qty -->
            <div class="wdp-dtc wdp-col-qty wdp-text-right">
              <template v-if="!line.canApply">
                <span class="wdp-muted">—</span>
              </template>
              <template v-else-if="line.changeType === 'MaterialReplaced'">
                <span class="wdp-preserved">— (preserved)</span>
              </template>
              <template v-else-if="line.changeType === 'NoChange'">
                <span class="wdp-muted">—</span>
              </template>
              <template v-else>
                <span class="wdp-new-qty">{{ line.newUnitQty != null ? formatQty(line.newUnitQty) : '—' }}</span>
              </template>
            </div>
            <!-- UOM -->
            <div class="wdp-dtc wdp-col-uom">
              <span :class="line.uom ? '' : 'wdp-muted'">{{ line.uom || '—' }}</span>
            </div>
            <!-- Change -->
            <div class="wdp-dtc wdp-col-chg">
              <template v-if="!line.canApply">
                <div class="wdp-na-cell">
                  <span class="wdp-warn-icon">&#9888;</span>
                  <span class="wdp-na-reason">{{ line.blockReason || 'Not applicable' }}</span>
                </div>
              </template>
              <template v-else>
                <span :class="['wdp-pill', 'wdp-pill-' + pillColor(line.changeType)]">
                  {{ changeLabel(line.changeType) }}
                </span>
              </template>
            </div>
          </div>
        </div>
      </div>

      <!-- Panel footer -->
      <div class="wdp-foot">
        <span>
          {{ affectedCount }} of {{ wo.lines.length }} {{ wo.lines.length === 1 ? 'line' : 'lines' }} affected
          <template v-if="notApplicableCount > 0"> · {{ notApplicableCount }} skipped</template>
        </span>
        <span v-if="!isReadOnly" :class="isExcluded ? 'wdp-incl-wrap' : 'wdp-excl-wrap'">
          <DxButton
            :text="isExcluded ? 'Include this work order' : 'Exclude this work order'"
            type="normal"
            styling-mode="outlined"
            class="wdp-toggle-btn"
            @click="onToggleExclusion"
          />
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import DxButton from 'devextreme-vue/button';
import { formatNumber } from 'devextreme/localization';

export default {
  name: 'GcWoBulkUpdateWoDiffPanel',
  components: { DxButton },
  props: {
    wo: { type: Object, required: true },
    isExcluded: { type: Boolean, default: false },
    isReadOnly: { type: Boolean, default: false },
  },
  data() {
    return {
      expanded: false,
    };
  },
  computed: {
    hasNoChanges() {
      return this.wo.lines.every(function(l) {
        return !l.canApply || l.changeType === 'NoChange';
      });
    },
    summaryPills() {
      var lines = this.wo.lines;
      var replaced = lines.filter(function(l) {
        return l.changeType === 'MaterialReplaced' || l.changeType === 'MaterialAndQtyReplaced';
      }).length;
      var qtyChanged = lines.filter(function(l) {
        return l.changeType === 'QtyChanged';
      }).length;
      var na = lines.filter(function(l) { return !l.canApply; }).length;
      var pills = [];
      if (replaced) pills.push({ text: replaced + ' replacement' + (replaced > 1 ? 's' : ''), color: 'green' });
      if (qtyChanged) pills.push({ text: qtyChanged + ' qty change' + (qtyChanged > 1 ? 's' : ''), color: 'amber' });
      if (na) pills.push({ text: na + ' skipped', color: 'red' });
      return pills;
    },
    affectedCount() {
      return this.wo.lines.filter(function(l) {
        return l.canApply && l.changeType !== 'NoChange';
      }).length;
    },
    notApplicableCount() {
      return this.wo.lines.filter(function(l) { return !l.canApply; }).length;
    },
  },
  methods: {
    isMaterialChanged(line) {
      return line.changeType === 'MaterialReplaced' || line.changeType === 'MaterialAndQtyReplaced';
    },
    onToggleExclusion() {
      this.$emit(this.isExcluded ? 'include' : 'exclude', this.wo.wono);
    },
    woStatusLabel(s) {
      var map = {
        D: 'Draft',
        P: 'Planning',
        A: 'Awaiting Production',
        I: 'In Production',
        R: 'Review',
        C: 'Complete',
        X: 'Cancelled',
      };
      return map[(s || '').trim()] || s;
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      var d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      var months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return d.getDate() + ' ' + months[d.getMonth()] + ' ' + d.getFullYear();
    },
    rowClass(line) {
      if (!line.canApply) return 'wdp-row-na';
      if (line.changeType === 'MaterialReplaced' || line.changeType === 'MaterialAndQtyReplaced') return 'wdp-row-replaced';
      if (line.changeType === 'QtyChanged') return 'wdp-row-changed';
      return 'wdp-row-nochange';
    },
    pillColor(changeType) {
      if (changeType === 'MaterialReplaced' || changeType === 'MaterialAndQtyReplaced') return 'green';
      if (changeType === 'QtyChanged') return 'amber';
      return 'grey';
    },
    changeLabel(changeType) {
      var labels = {
        QtyChanged: 'Qty changed',
        MaterialReplaced: 'Replaced',
        MaterialAndQtyReplaced: 'Replaced',
        NoChange: 'No change',
      };
      return labels[changeType] || changeType;
    },
    formatQty(value) {
      if (value == null) return '—';
      return formatNumber(value, '##,###,###,###,##0.0000');
    },
  },
};
</script>

<style lang="scss">
.wdp-panel {
  border: 1px solid #dee2e6;
  border-radius: 6px;
  overflow: hidden;
  transition: border-color 0.12s;

  &:hover {
    border-color: #b0d4ea;
  }

  &.wdp-excluded {
    opacity: 0.55;
    border-style: dashed;
  }
}

// ── Header ────────────────────────────────────────────────────────────────────
.wdp-hdr {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  cursor: pointer;
  background: #fafafa;
  user-select: none;

  &:hover {
    background: #f0f8ff;
  }
}

.wdp-hdr-left {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.wdp-chevron {
  font-size: 10px;
  color: #6c757d;
  flex-shrink: 0;
}

.wdp-title {
  font-size: 13px;
  font-weight: 600;
  color: #101828;
}

.wdp-wono {
  color: #03a9f4;
}

.wdp-sep,
.wdp-wo-desc {
  color: #344054;
}

.wdp-sub {
  font-size: 11px;
  color: #6c757d;
  margin-top: 1px;
}

.wdp-hdr-right {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  margin-left: 12px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.wdp-nochange-text {
  font-size: 12px;
  color: #6c757d;
}

// ── Badges ─────────────────────────────────────────────────────────────────────
.wdp-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
}

.wdp-badge-excluded {
  background: #f8f9fa;
  color: #6c757d;
  border: 1px solid #adb5bd;
}

// ── Summary pills ─────────────────────────────────────────────────────────────
.wdp-pill {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
}

.wdp-pill-green {
  background: #dcfce7;
  color: #166534;
  border: 1px solid #86efac;
}

.wdp-pill-amber {
  background: #fef0c7;
  color: #92400e;
  border: 1px solid #f79009;
}

.wdp-pill-red {
  background: #fee4e2;
  color: #9b1c1c;
  border: 1px solid #f97066;
}

.wdp-pill-grey {
  background: #f8f9fa;
  color: #6c757d;
  border: 1px solid #dee2e6;
}

// ── Body ──────────────────────────────────────────────────────────────────────
.wdp-body {
  border-top: 1px solid #dee2e6;
}

// ── Diff table ────────────────────────────────────────────────────────────────
.wdp-dtable-wrap {
  overflow-x: auto;
}

.wdp-dtable {
  width: 100%;
  font-size: 12px;
  min-width: 780px;
}

.wdp-dtable-head {
  display: flex;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.wdp-dth {
  padding: 6px 10px;
  font-size: 11px;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  white-space: nowrap;
}

.wdp-dtr {
  display: flex;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }

  &.wdp-row-replaced {
    background: #f0fdf4;
  }

  &.wdp-row-changed {
    background: #fffbeb;
  }

  &.wdp-row-na {
    background: #fff8f8;
    opacity: 0.85;
  }

  &.wdp-row-nochange {
    background: #fafafa;
  }
}

.wdp-dtc {
  padding: 8px 10px;
  color: #344054;
  overflow: hidden;
}

// ── Column widths ─────────────────────────────────────────────────────────────
.wdp-col-process {
  width: 100px;
  flex-shrink: 0;
}

.wdp-col-code {
  width: 130px;
  flex-shrink: 0;
}

.wdp-col-desc {
  flex: 1;
  min-width: 140px;
}

.wdp-col-qty {
  width: 120px;
  flex-shrink: 0;
}

.wdp-col-uom {
  width: 60px;
  flex-shrink: 0;
}

.wdp-col-chg {
  width: 150px;
  flex-shrink: 0;
}

.wdp-text-right {
  text-align: right;
}

// ── Old / new value cells ────────────────────────────────────────────────────
.wdp-old-val {
  font-size: 11px;
  color: #adb5bd;
  text-decoration: line-through;
  line-height: 1.4;
}

.wdp-new-code {
  font-size: 12px;
  font-weight: 600;
  color: #166534;
  line-height: 1.4;
}

.wdp-new-desc {
  font-size: 12px;
  color: #166534;
  line-height: 1.4;
}

.wdp-preserved {
  font-size: 11px;
  color: #adb5bd;
  font-style: italic;
}

.wdp-new-qty {
  font-weight: 600;
  color: #101828;
}

.wdp-muted {
  color: #adb5bd;
}

// ── Not-applicable cell ───────────────────────────────────────────────────────
.wdp-na-cell {
  display: flex;
  align-items: flex-start;
  gap: 4px;
}

.wdp-warn-icon {
  color: #dc6803;
  font-size: 12px;
  flex-shrink: 0;
  line-height: 1.4;
}

.wdp-na-reason {
  font-size: 11px;
  color: #92400e;
  line-height: 1.4;
  white-space: normal;
}

// ── Footer ────────────────────────────────────────────────────────────────────
.wdp-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-top: 1px solid #f0f0f0;
  background: #fafafa;
  font-size: 12px;
  color: #6c757d;
}

.wdp-toggle-btn {
  font-size: 12px;
}

.wdp-incl-wrap .dx-button {
  color: #166534;
  border-color: #86efac;

  &:hover {
    background: #f0fdf4;
  }
}
</style>
